import { supabase } from "@/lib/supabase";
import { INTERNAL_TO_PUBLIC } from "@/lib/vda";
import { api } from "@/lib/api";

const BOOKING_SELECT =
  "*, vehicles(registration, make, model, mot_due_date, service_due_date), drivers(full_name, mobile_number, email), " +
  "customers(name, customer_code, pod_id, shared_mailbox, support_phone, emergency_phone, instructions, pods(name)), " +
  "assignee:user_profiles!assigned_staff_user_id(full_name)";

export async function loadStaffBookings() {
  const { data, error } = await supabase
    .from("booking_requests")
    .select(BOOKING_SELECT)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return data || [];
}

// Write-authority context, mirroring the SQL can_write_booking() exactly so the
// UI only ever offers mutation controls that will actually succeed (fixes the
// booking_updates RLS 403 on cross-pod records). No RLS change — least privilege.
export async function loadWriteContext(uid) {
  if (!uid) return null;
  const [{ data: prof }, sca, amc, lead, coverIds] = await Promise.all([
    supabase.from("user_profiles").select("role, home_pod_id, active").eq("id", uid).maybeSingle(),
    supabase.from("staff_customer_assignments").select("customer_id").eq("staff_user_id", uid).eq("active", true),
    supabase.from("customers").select("id").eq("account_manager_user_id", uid),
    supabase.from("pods").select("id").eq("pod_lead_user_id", uid),
    // Own active cover from a trusted backend endpoint (avoids cover-SELECT RLS gaps across roles)
    api.myCover().then((r) => r.booking_ids || []).catch(() => []),
  ]);
  const managed = new Set([...(sca.data || []).map((r) => r.customer_id), ...(amc.data || []).map((r) => r.id)]);
  return {
    uid,
    role: prof?.role,
    active: !!prof?.active,
    homePodId: prof?.home_pod_id || null,
    isAdmin: prof?.role === "administrator",
    managedCustomerIds: managed,
    leadPodIds: new Set((lead.data || []).map((r) => r.id)),
    coverBookingIds: new Set(coverIds || []),
  };
}

export function canWriteBooking(b, ctx) {
  if (!b || !ctx || !ctx.active) return false;
  if (ctx.isAdmin) return true;
  if (b.customer_id && ctx.managedCustomerIds.has(b.customer_id)) return true; // assigned customer / AM
  if (b.pod_id && ctx.leadPodIds.has(b.pod_id)) return true;                   // pod lead of the pod
  if (b.assigned_staff_user_id === ctx.uid) return true;                       // individual assignee
  if (ctx.coverBookingIds.has(b.id)) return true;                             // active cover (additive, scoped)
  // Corrected model (0025): an ACTIVE internal-staff member of the booking's OWNING
  // pod may operate its own pod's work — assignment is accountability, not a write gate.
  if (b.pod_id && ctx.homePodId && b.pod_id === ctx.homePodId) return true;
  return false;
}

// Sprint 6 Phase B: home-pod membership grants VISIBILITY; explicit "Assign to me"
// grants write authority. A home-pod member (or a manager/lead) may self-assign an
// UNASSIGNED booking to take ownership — the backend permits self-assign for pod
// members (server.py assign_booking: is_pod_member).
export function canSelfAssign(b, ctx) {
  if (!b || !ctx || !ctx.active) return false;
  if (b.assigned_staff_user_id) return false;
  if (["completed", "cancelled"].includes(b.internal_status)) return false;
  if (canWriteBooking(b, ctx)) return true;
  return !!(b.pod_id && ctx.homePodId && b.pod_id === ctx.homePodId);
}


export async function loadBooking(id) {
  const { data } = await supabase.from("booking_requests").select(BOOKING_SELECT).eq("id", id).limit(1);
  return data?.[0] || null;
}

export async function loadTimeline(id) {
  const { data } = await supabase
    .from("booking_updates")
    .select("*, author:user_profiles!created_by_user_id(full_name)")
    .eq("booking_request_id", id)
    .order("created_at", { ascending: false });
  return data || [];
}

export async function loadAttachments(id) {
  const { data } = await supabase.from("booking_attachments").select("*").eq("booking_request_id", id);
  return data || [];
}

export async function loadEmailEvents(id) {
  const { data } = await supabase.from("email_events").select("*").eq("booking_request_id", id).order("created_at", { ascending: false });
  return data || [];
}

export async function loadRefData() {
  const [{ data: customers }, { data: pods }, { data: staff }] = await Promise.all([
    supabase.from("customers").select("id, name, customer_code, pod_id, hire_policy"),
    supabase.from("pods").select("id, name"),
    supabase.from("user_profiles").select("id, full_name, role, home_pod_id, active").in("role", ["account_executive", "senior_account_executive", "account_manager", "pod_lead", "administrator"]),
  ]);
  return { customers: customers || [], pods: pods || [], staff: staff || [] };
}

export async function loadAccessibleVehicles() {
  const { data } = await supabase
    .from("vehicles")
    .select("id, registration, make, model, customer_id, customers(name)")
    .order("registration", { ascending: true });
  return data || [];
}

// Read-only allocation history for a vehicle, derived from driver_allocations.
export async function loadAllocationHistory(vehicleId) {
  const { data: allocs } = await supabase
    .from("driver_allocations")
    .select("*, drivers(full_name)")
    .eq("vehicle_id", vehicleId)
    .order("start_at", { ascending: false });
  const allocations = allocs || [];
  const ids = allocations.map((a) => a.id);
  let audits = [], batches = [], actors = [];
  if (ids.length) {
    const { data: au } = await supabase
      .from("audit_events")
      .select("entity_id, action, actor_user_id, created_at")
      .eq("entity_type", "driver_allocation")
      .in("entity_id", ids);
    audits = au || [];
  }
  const batchIds = [...new Set(allocations.map((a) => a.source_batch_id).filter(Boolean))];
  if (batchIds.length) {
    const { data: b } = await supabase.from("import_batches").select("id, file_name").in("id", batchIds);
    batches = b || [];
  }
  const actorIds = [...new Set(audits.map((a) => a.actor_user_id).filter(Boolean))];
  if (actorIds.length) {
    const { data: p } = await supabase.from("user_profiles").select("id, full_name").in("id", actorIds);
    actors = p || [];
  }
  const batchMap = Object.fromEntries(batches.map((b) => [b.id, b.file_name]));
  const actorMap = Object.fromEntries(actors.map((a) => [a.id, a.full_name]));
  return allocations.map((a) => {
    const created = audits.find((x) => x.entity_id === a.id && x.action === "allocation_created");
    const ended = audits.find((x) => x.entity_id === a.id && x.action === "allocation_ended");
    return {
      ...a,
      driver_name: a.drivers?.full_name || "—",
      import_file: a.source_batch_id ? (batchMap[a.source_batch_id] || "Imported batch") : null,
      approved_by: created ? actorMap[created.actor_user_id] : null,
      ended_by: ended ? actorMap[ended.actor_user_id] : null,
    };
  });
}


// Apply a status change: patch booking + append timeline record (audit)
export async function applyStatus(booking, { internal_status, public_status, internal_note, public_message, next_update_due_at, extra, source = "individual", uid }) {
  const patch = { ...(extra || {}) };
  if (internal_status) {
    patch.internal_status = internal_status;
    patch.public_status = public_status || INTERNAL_TO_PUBLIC[internal_status] || booking.public_status;
    if (internal_status === "completed") patch.completed_at = new Date().toISOString();
  }
  if (next_update_due_at !== undefined) patch.next_update_due_at = next_update_due_at;
  if (Object.keys(patch).length) {
    const { error } = await supabase.from("booking_requests").update(patch).eq("id", booking.id);
    if (error) throw error;
  }
  const { error: e2 } = await supabase.from("booking_updates").insert({
    booking_request_id: booking.id,
    update_type: internal_status ? "status_change" : (public_message ? "message" : "note"),
    previous_internal_status: booking.internal_status,
    internal_status: internal_status || null,
    public_status: internal_status ? (patch.public_status) : (public_status || null),
    internal_note: internal_note || null,
    public_message: public_message || null,
    created_by_user_id: uid,
    source,
  });
  if (e2) throw e2;
}
